import React from "react";
import { useNavigate } from "react-router-dom";

export type LinkedAppointmentSummary = {
  _id?: string;
  sessionName?: string;
  date?: string | Date;
  startTime?: string;
  endTime?: string;
  queue?: number | string;
  status?: string;
  purposeLabel?: string;
};

export function formatLinkedAppointmentTime(
  appointment?: LinkedAppointmentSummary | null
): string {
  if (!appointment) return "";
  const start = String(appointment.startTime ?? "").trim();
  const end = String(appointment.endTime ?? "").trim();
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

export function formatLinkedAppointmentPurpose(
  appointment?: LinkedAppointmentSummary | null
): string {
  if (!appointment) return "";
  return String(appointment.purposeLabel ?? "").trim();
}

/** Token/queue only when generated (queue > 0). */
export function formatLinkedAppointmentToken(
  appointment?: LinkedAppointmentSummary | null
): string {
  if (!appointment) return "";
  const q = Number(appointment.queue);
  if (!Number.isFinite(q) || q <= 0) return "";
  return String(Math.floor(q));
}

/**
 * Purpose · Tok N · time — only parts that exist (blank when unlinked / empty).
 */
export function formatLinkedAppointmentVisitLabel(
  appointment?: LinkedAppointmentSummary | null
): string {
  if (!appointment) return "";
  const purpose = formatLinkedAppointmentPurpose(appointment);
  const token = formatLinkedAppointmentToken(appointment);
  const time = formatLinkedAppointmentTime(appointment);
  return [purpose || null, token ? `Tok ${token}` : null, time || null]
    .filter(Boolean)
    .join(" · ");
}

/** Calendar day for linked appointment; empty when unlinked. */
export function formatLinkedAppointmentDate(
  appointment?: LinkedAppointmentSummary | null
): string {
  if (!appointment?.date) return "";
  const value = appointment.date;
  if (typeof value === "string") {
    const plain = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (plain) return plain[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value);
    return raw.includes("T") ? raw.split("T")[0] : raw.slice(0, 10);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatApptDate(value?: string | Date): string {
  return formatLinkedAppointmentDate({ date: value } as LinkedAppointmentSummary);
}

/** Compact chip for invoice/payment rows when an appointment is linked. */
export default function LinkedAppointmentChip({
  appointment,
}: {
  appointment?: LinkedAppointmentSummary | null;
}) {
  const navigate = useNavigate();
  if (!appointment?._id) return null;

  const session = String(appointment.sessionName ?? "").trim();
  const time = formatLinkedAppointmentTime(appointment);
  const day = formatApptDate(appointment.date);
  const purpose = formatLinkedAppointmentPurpose(appointment);
  const label = ["Appt", session || null, time || day || null, purpose || null]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      title="Open appointment list for this date"
      onClick={(e) => {
        e.stopPropagation();
        const qs = day ? `?date=${encodeURIComponent(day)}` : "";
        navigate(`/client/view-appointment${qs}`);
      }}
      className="mt-0.5 inline-flex max-w-full items-center truncate rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300 dark:hover:bg-sky-900/60"
    >
      {label}
    </button>
  );
}
